const DB_NAME = 'shiboruu-avatar-cache'
const DB_VERSION = 2
const STORE_NAME = 'avatars'
const DEFAULT_AVATAR_STORE_NAME = 'defaultAvatars'

// 同じ DB を何度も open しないよう、初回の open 処理を使い回す。
let databasePromise: Promise<IDBDatabase | null> | null = null

/**
 * IndexedDB に保存するアバターキャッシュのエントリ形式。
 *
 * `userId` が keyPath であり、1 ユーザー 1 エントリの上書き方式で管理する。
 */
export type AvatarCacheEntry = {
  /** Firebase UID。IndexedDB の keyPath として使用 */
  userId: string
  /** D1 の `user_avatars.avatar_version` と同値。サーバとの変更検知に使用 */
  avatarVersion: string
  /** VRM バイナリ本体（最大 50 MB） */
  vrmBlob: Blob
  /** キャッシュ保存時刻（ms）。`Date.now()` の値 */
  cachedAt: number
}

/**
 * IndexedDB に保存するデフォルトアバターキャッシュのエントリ形式。
 *
 * `avatarName` が keyPath であり、公開名ごとに 1 エントリで管理する。
 */
export type DefaultAvatarCacheEntry = {
  /** 公開 default avatar 名。IndexedDB の keyPath として使用 */
  avatarName: string
  /** default avatar API が返す `ETag`。再検証に使用 */
  avatarVersion: string
  /** VRM バイナリ本体 */
  vrmBlob: Blob
  /** キャッシュ初回保存時刻（ms） */
  cachedAt: number
  /** 最新の 200 / 304 応答で妥当性を確認した時刻（ms） */
  validatedAt: number
}

/**
 * 現在の実行環境で IndexedDB を利用できるか判定する。
 *
 * ブラウザ以外の環境や、IndexedDB を提供しないテスト環境では
 * `false` を返し、呼び出し側は永続化をスキップする。
 *
 * @returns IndexedDB が利用可能な場合は `true`
 */
const canUseIndexedDb = (): boolean => typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined'

const createRequestPromise = <T>(request: IDBRequest<T>, message: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      reject(request.error ?? new Error(message))
    }
  })

/**
 * アバターキャッシュ用の IndexedDB を開く。
 *
 * 初回呼び出し時にデータベースと object store を初期化し、
 * 以降は同じ open 処理の Promise を再利用する。
 *
 * @returns 利用可能な場合は `IDBDatabase`、未対応環境では `null`
 * @throws データベースの open に失敗した場合
 */
const openAvatarCacheDatabase = async (): Promise<IDBDatabase | null> => {
  if (!canUseIndexedDb()) return null
  if (databasePromise !== null) return databasePromise

  databasePromise = new Promise<IDBDatabase | null>((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        // userId を keyPath に設定し、1 ユーザー 1 エントリで管理する。
        database.createObjectStore(STORE_NAME, { keyPath: 'userId' })
      }
      if (!database.objectStoreNames.contains(DEFAULT_AVATAR_STORE_NAME)) {
        database.createObjectStore(DEFAULT_AVATAR_STORE_NAME, { keyPath: 'avatarName' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('failed to open avatar cache database'))
  }).catch((error) => {
    databasePromise = null
    throw error
  })

  return databasePromise
}

/**
 * 指定ユーザーのアバターキャッシュエントリを取得する。
 *
 * キャッシュが存在しない場合や IndexedDB が利用できない環境では
 * `undefined` を返す。
 *
 * @param userId Firebase UID
 * @returns キャッシュエントリ。存在しない場合は `undefined`
 * @throws 読み込みに失敗した場合
 */
export const getAvatarCache = async (userId: string): Promise<AvatarCacheEntry | undefined> => {
  const database = await openAvatarCacheDatabase()
  if (database === null) return undefined

  const transaction = database.transaction(STORE_NAME, 'readonly')
  const request = transaction.objectStore(STORE_NAME).get(userId)
  return (await createRequestPromise(request, 'failed to get avatar cache')) as AvatarCacheEntry | undefined
}

/**
 * アバターキャッシュエントリを保存（上書き）する。
 *
 * 同じ `userId` のエントリが存在する場合は上書きされる。
 *
 * @param entry 保存するキャッシュエントリ
 * @returns 保存完了後に resolve される Promise
 * @throws 保存に失敗した場合
 */
export const setAvatarCache = async (entry: AvatarCacheEntry): Promise<void> => {
  const database = await openAvatarCacheDatabase()
  if (database === null) return

  const transaction = database.transaction(STORE_NAME, 'readwrite')
  // keyPath が 'userId' なので put() で自動的に上書きされる。
  const request = transaction.objectStore(STORE_NAME).put(entry)
  await createRequestPromise(request, 'failed to set avatar cache')
}

/**
 * 指定ユーザーのアバターキャッシュエントリを削除する。
 *
 * エントリが存在しない場合も正常終了する。
 * サーバ側でアバターが削除された際（`GET /api/avatar` が 204 を返した場合）や
 * サーバ側の DELETE 成功後に呼び出す。
 *
 * @param userId Firebase UID
 * @returns 削除完了後に resolve される Promise
 * @throws 削除に失敗した場合
 */
export const deleteAvatarCache = async (userId: string): Promise<void> => {
  const database = await openAvatarCacheDatabase()
  if (database === null) return

  const transaction = database.transaction(STORE_NAME, 'readwrite')
  const request = transaction.objectStore(STORE_NAME).delete(userId)
  await createRequestPromise(request, 'failed to delete avatar cache')
}

/**
 * 指定名の default avatar cache を取得する。
 *
 * @param avatarName default avatar 公開名
 * @returns キャッシュエントリ。存在しない場合は `undefined`
 */
export const getDefaultAvatarCache = async (avatarName: string): Promise<DefaultAvatarCacheEntry | undefined> => {
  const database = await openAvatarCacheDatabase()
  if (database === null) return undefined

  const transaction = database.transaction(DEFAULT_AVATAR_STORE_NAME, 'readonly')
  const request = transaction.objectStore(DEFAULT_AVATAR_STORE_NAME).get(avatarName)
  return (await createRequestPromise(request, 'failed to get default avatar cache')) as
    | DefaultAvatarCacheEntry
    | undefined
}

/**
 * default avatar cache を保存または上書きする。
 *
 * @param entry 保存する default avatar cache
 */
export const setDefaultAvatarCache = async (entry: DefaultAvatarCacheEntry): Promise<void> => {
  const database = await openAvatarCacheDatabase()
  if (database === null) return

  const transaction = database.transaction(DEFAULT_AVATAR_STORE_NAME, 'readwrite')
  const request = transaction.objectStore(DEFAULT_AVATAR_STORE_NAME).put(entry)
  await createRequestPromise(request, 'failed to set default avatar cache')
}

/**
 * default avatar cache の `validatedAt` だけを更新する。
 *
 * エントリが無い場合は何もしない。
 *
 * @param avatarName default avatar 公開名
 * @param validatedAt 最新検証時刻
 */
export const touchDefaultAvatarCache = async (avatarName: string, validatedAt: number): Promise<void> => {
  const current = await getDefaultAvatarCache(avatarName)
  if (!current) return

  await setDefaultAvatarCache({
    ...current,
    validatedAt,
  })
}

/**
 * 指定名の default avatar cache を削除する。
 *
 * @param avatarName default avatar 公開名
 */
export const deleteDefaultAvatarCache = async (avatarName: string): Promise<void> => {
  const database = await openAvatarCacheDatabase()
  if (database === null) return

  const transaction = database.transaction(DEFAULT_AVATAR_STORE_NAME, 'readwrite')
  const request = transaction.objectStore(DEFAULT_AVATAR_STORE_NAME).delete(avatarName)
  await createRequestPromise(request, 'failed to delete default avatar cache')
}
